import '../index.css'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { Button, Col, Collapse, Input, message, Modal, Row, Tag, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import { isMobile } from 'react-device-detect'
import { BellOutlined, CalendarOutlined, DownloadOutlined, WarningFilled } from '@ant-design/icons'
import { Column, ColumnConfig, Line, LineConfig } from '@ant-design/plots'
import { defaultEnd, defaultStart, PageProps } from '../'
import PageDrawer from '../../../components/Drawer/DatePickerDrawer'
import { Loading } from '../../../components/Loading/defaultLoading'
import { PaddingBox, PageSubtitle, PageTitle, TabHeader } from '../../../components/Wrapper'
import * as CONFIG from './chartConfig'
import { UserService } from '../../../services/user-services'
import { SummaryService } from '../../../services/summary-services'
import { NotificationService } from '../../../services/notification-services'
import { UserData } from '../../../types/user/types'
import { buildPatientExport, ExportBundle } from '../../../utils/exportPatientHistory'
import ExportPreviewModal from '../../../components/ExportPreviewModal'
import { dateCorrectFallback, wordsCorrectFallback } from '../../../utils/cognitionCriteria'

import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

const { Text } = Typography

/* ------------------------------ domain card ------------------------------ */

interface Metric {
  label: string
  value: React.ReactNode
  alert?: boolean
}

interface DomainCardProps {
  title: string
  metrics: Metric[] | null
  testDate?: string | null
  status?: string | null
  notRequired?: boolean
}

const statusTagColor = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'doing well':
      return 'cyan'
    case 'aim to improve':
      return 'orange'
    default:
      return 'default'
  }
}

const DomainCard: React.FC<DomainCardProps> = ({ title, metrics, testDate, status, notRequired }) => (
  <div className='domain_card'>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Text strong style={{ fontSize: 14 }}>
          {title}
        </Text>
        {testDate && (
          <Text type='secondary' style={{ fontSize: 11 }}>
            Tested {testDate}
          </Text>
        )}
      </div>
      {status && (
        <Tag color={statusTagColor(status)} style={{ marginInlineEnd: 0 }}>
          {status}
        </Tag>
      )}
    </div>
    {metrics && metrics.length > 0 ? (
      <div className='domain_card_metrics'>
        {metrics.map((metric) => (
          <div className='domain_card_metric' key={metric.label}>
            <Text type='secondary' style={{ fontSize: 12 }}>
              {metric.label}
            </Text>
            <Text style={{ fontSize: 15, fontWeight: 600, color: metric.alert ? '#d4380d' : undefined }}>
              {metric.alert && <WarningFilled style={{ marginRight: 4 }} />}
              {metric.value}
            </Text>
          </div>
        ))}
      </div>
    ) : (
      <Text type='secondary' style={{ fontSize: 13 }}>
        {notRequired ? 'Not required for this patient' : 'No data yet'}
      </Text>
    )}
  </div>
)

/* ------------------------------ chart card ------------------------------ */

interface ChartCardProps {
  title: string
  description: string
  children: React.ReactNode
}

const ChartCard: React.FC<ChartCardProps> = ({ title, description, children }) => (
  <div className='report_chart_container' style={{ padding: isMobile ? 16 : 24 }}>
    <Text style={{ fontSize: 16, fontWeight: 600 }}>{title}</Text>
    <br />
    <Text type='secondary' style={{ fontSize: 12 }}>
      {description}
    </Text>
    <div style={{ marginTop: 16 }}>{children}</div>
  </div>
)

/* --------------------------------- main fc -------------------------------- */

const Report: React.FC<PageProps & { setReady: React.Dispatch<React.SetStateAction<boolean>> }> = ({
  userName,
  setReady,
}) => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs(defaultStart, 'YYYY-MM-DD'),
    dayjs(defaultEnd, 'YYYY-MM-DD'),
  ])
  const [drawer, setDrawer] = useState(false)
  const dateString = dateRange[0].format('DD MMM YYYY') + ' - ' + dateRange[1].format('DD MMM YYYY')

  const [loading, setLoading] = useState(true)
  const [exportPreview, setExportPreview] = useState<ExportBundle | null>(null)
  const [reportsData, setReportsData] = useState<UserData | null>()

  const showDrawer = useCallback(() => setDrawer(true), [setDrawer])
  const closeDrawer = useCallback(() => setDrawer(false), [setDrawer])

  const userService = UserService.getInstance()
  const summaryService = SummaryService.getInstance()
  const notificationService = NotificationService.getInstance()

  const [reminderOpen, setReminderOpen] = useState(false)
  const [reminderSending, setReminderSending] = useState(false)
  const [reminderMessage, setReminderMessage] = useState('')
  const [reminderTitle, setReminderTitle] = useState('')
  const [rank1Status, setRank1Status] = useState<string | null>(null)
  const [rank2Status, setRank2Status] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      const queryUser: UserData = {
        userId: userName || '',
        email: '',
        cognition: [],
        ad8: [],
        sitToStand: [],
        nutrition: [],
        vision: [],
        hearing: [],
        mood: [],
        handgrip: [],
        exhaustion: [],
        gaitSpeed: [],
        physicalActivity: [],
        bloodPressure: [],
        creationTimestamp: '',
        lastUpdatedTimestamp: '',
      }
      const [userData, rank1, rank2] = await Promise.all([
        userService.get(queryUser),
        summaryService.getRank1(queryUser),
        summaryService.getRank2(queryUser),
      ])
      setReportsData(userData)
      setRank1Status(rank1)
      setRank2Status(rank2)
      setLoading(false)
    }
    fetchData()
  }, [userName, userService, summaryService])

  useEffect(() => {
    if (!loading) setReady(true)
  }, [loading, setReady])

  const exportHistory = () => {
    if (reportsData) setExportPreview(buildPatientExport(reportsData, (reportsData as any).username || userName))
  }

  if (loading) return <Loading />
  if (!reportsData) return null

  const latestCognition = reportsData.cognition[reportsData.cognition.length - 1]
  const latestSitToStand = reportsData.sitToStand[reportsData.sitToStand.length - 1]
  const latestNutrition = reportsData.nutrition[reportsData.nutrition.length - 1]
  const latestVision = reportsData.vision[reportsData.vision.length - 1]
  const latestHearing = reportsData.hearing[reportsData.hearing.length - 1]
  const latestMood = reportsData.mood[reportsData.mood.length - 1]
  const latestAd8 = reportsData.ad8[reportsData.ad8.length - 1]
  const latestHandgrip = reportsData.handgrip[reportsData.handgrip.length - 1]
  const latestLeftHandgrip = [...reportsData.handgrip].reverse().find((record: any) => record.leftHandReading)
  const latestRightHandgrip = [...reportsData.handgrip].reverse().find((record: any) => record.rightHandReading)
  const latestExhaustion = reportsData.exhaustion[reportsData.exhaustion.length - 1]
  const latestGaitSpeed = reportsData.gaitSpeed[reportsData.gaitSpeed.length - 1]
  const latestPhysicalActivity = reportsData.physicalActivity[reportsData.physicalActivity.length - 1]

  const recordDate = (record: any): string | null =>
    record?.creationTimestamp ? dayjs(record.creationTimestamp).format('DD MMM YYYY') : null

  const recordStatus = (record: any): string | null => record?.otherInformation?.statusLabel ?? null

  const sitToStandTime = (record: any): number | null =>
    record?.otherInformation?.activeDuration ?? record?.otherInformation?.totalTimeTaken ?? null

  const latestSitToStandTime = sitToStandTime(latestSitToStand)

  const recalledWordsAnswer = (latestCognition as any)?.recalledThreeWordsInOneTextbox
    ? String((latestCognition as any).recalledThreeWordsInOneTextbox)
    : (latestCognition?.recalledThreeWords ?? []).join(', ')

  const cognitionInfo = (latestCognition?.otherInformation ?? {}) as any
  const cognitionDateCorrect =
    typeof cognitionInfo.dateCorrect === 'boolean'
      ? cognitionInfo.dateCorrect
      : dateCorrectFallback((latestCognition as any)?.selectedDate, (latestCognition as any)?.creationTimestamp)
  const cognitionWordsCorrect =
    typeof cognitionInfo.threeWordsCorrect === 'boolean'
      ? cognitionInfo.threeWordsCorrect
      : wordsCorrectFallback(latestCognition)
  const cognitionLocationCorrect =
    typeof cognitionInfo.locationCorrect === 'boolean' ? cognitionInfo.locationCorrect : null

  const isExhaustedAnswer = (answer: string | undefined) =>
    !!answer &&
    ['moderate amount of the time (3 \u2013 4 days)', 'most of the time'].includes(answer.toLowerCase())

  const hearingAidReported = !!latestHearing?.otherInformation?.wearingHearingAid

  const hearingTrialsPassed = latestHearing?.digits?.length
    ? latestHearing.digits.filter((trial: string, x: number) =>
        Array.from(trial).every((digit, y) => digit === latestHearing.userResponse?.[x]?.[y])
      ).length
    : 0

  const cognitionStatus = recordStatus(latestCognition)
  const ad8Required = !(latestCognition && cognitionStatus === 'Doing Well')

  const domains: DomainCardProps[] = [
    {
      title: 'Cognition',
      testDate: recordDate(latestCognition),
      status: cognitionStatus,
      metrics: latestCognition
        ? [
            {
              label: 'Reported Memory Issue',
              value: latestCognition.havingIssueWithMemory ? 'Yes' : 'No',
              alert: !!latestCognition.havingIssueWithMemory,
            },
            {
              label: 'Word Recall',
              value: recalledWordsAnswer
                ? recalledWordsAnswer.length > 40
                  ? `${recalledWordsAnswer.slice(0, 40)}…`
                  : recalledWordsAnswer
                : 'No answer',
              alert: !recalledWordsAnswer || cognitionWordsCorrect === false,
            },
            ...(cognitionDateCorrect !== null
              ? [
                  {
                    label: 'Stated Date',
                    value: cognitionDateCorrect ? 'Correct' : 'Wrong',
                    alert: !cognitionDateCorrect,
                  },
                ]
              : []),
            ...(cognitionLocationCorrect !== null
              ? [
                  {
                    label: 'Stated Location',
                    value: cognitionLocationCorrect ? 'Correct' : 'Wrong',
                    alert: !cognitionLocationCorrect,
                  },
                ]
              : []),
          ]
        : null,
    },
    {
      title: 'Mobility',
      testDate: recordDate(latestSitToStand),
      status: latestSitToStand ? recordStatus(latestSitToStand) ?? 'Unable to Predict' : null,
      metrics: latestSitToStand
        ? (() => {
            const finished5ChairRises = latestSitToStand.otherInformation?.finished5ChairRises
            return [
              ...(typeof finished5ChairRises === 'boolean'
                ? [
                    {
                      label: 'Completed 5 Rises',
                      value: finished5ChairRises ? 'Yes' : 'No',
                      alert: !finished5ChairRises,
                    },
                  ]
                : []),
              {
                label: 'Time Taken',
                value:
                  finished5ChairRises === false
                    ? '-'
                    : latestSitToStandTime != null
                      ? `${latestSitToStandTime.toFixed(2)}s`
                      : '-',
              },
            ]
          })()
        : null,
    },
    {
      title: 'Nutrition',
      testDate: recordDate(latestNutrition),
      status: recordStatus(latestNutrition),
      metrics: latestNutrition
        ? [
            {
              label: 'Loss of Weight',
              value: latestNutrition.havingLossOfWeight ? 'Yes' : 'No',
              alert: !!latestNutrition.havingLossOfWeight,
            },
            {
              label: 'Loss of Appetite',
              value: latestNutrition.havingLossOfAppetite ? 'Yes' : 'No',
              alert: !!latestNutrition.havingLossOfAppetite,
            },
          ]
        : null,
    },
    {
      title: 'Vision',
      testDate: recordDate(latestVision),
      status: recordStatus(latestVision),
      metrics: latestVision
        ? 'havingEyeDiseases' in latestVision
          ? [
              {
                label: 'Eye Diseases',
                value: latestVision.havingEyeDiseases ? 'Yes' : 'No',
                alert: !!latestVision.havingEyeDiseases,
              },
              {
                label: 'Difficulty Seeing Far',
                value: latestVision.havingDifficultySeeingFar ? 'Yes' : 'No',
                alert: !!latestVision.havingDifficultySeeingFar,
              },
              {
                label: 'On Treatment',
                value: latestVision.onTreatment ? 'Yes' : 'No',
                alert: !!latestVision.onTreatment,
              },
            ]
          : [
              {
                label: 'Vision Problem',
                value: latestVision.havingEyeProblems ? 'Yes' : 'No',
                alert: !!latestVision.havingEyeProblems,
              },
            ]
        : null,
    },
    {
      title: 'Hearing',
      testDate: recordDate(latestHearing),
      status: recordStatus(latestHearing),
      metrics: latestHearing
        ? [
            {
              label: 'Hearing Aid Reported',
              value: hearingAidReported ? 'Yes' : 'No',
            },
            ...(latestHearing?.digits?.length > 0
              ? [
                  {
                    label: 'Trials Passed',
                    value: hearingAidReported ? '-' : `${hearingTrialsPassed} / ${latestHearing.digits.length}`,
                    alert: !hearingAidReported && recordStatus(latestHearing) !== 'Doing Well',
                  },
                ]
              : []),
          ]
        : null,
    },
    {
      title: 'Mood',
      testDate: recordDate(latestMood),
      status: recordStatus(latestMood),
      metrics: latestMood
        ? [
            { label: 'Feeling Down', value: latestMood.feelingDown ? 'Yes' : 'No', alert: !!latestMood.feelingDown },
            {
              label: 'Little Interest',
              value: latestMood.experiencingLittleInterest ? 'Yes' : 'No',
              alert: !!latestMood.experiencingLittleInterest,
            },
          ]
        : null,
    },
    {
      title: 'AD8 (Memory Screening)',
      testDate: ad8Required ? recordDate(latestAd8) : null,
      status: ad8Required ? recordStatus(latestAd8) : 'Not Required',
      notRequired: !ad8Required,
      metrics: ad8Required && latestAd8
        ? (() => {
            const answers = [
              latestAd8.problemsWithJudgment,
              latestAd8.lessInterestInHobbies,
              latestAd8.repeatTheSameThings,
              latestAd8.troubleLearningTools,
              latestAd8.forgetCorrectDate,
              latestAd8.troubleHandlingFinancials,
              latestAd8.troubleRememberingAppointments,
              latestAd8.dailyProblemsWithThinking,
            ]
            const yesCount = answers.filter((answer) => String(answer).toLowerCase() === 'yes').length
            return [{ label: 'Score', value: `${yesCount} / ${answers.length}`, alert: yesCount >= 2 }]
          })()
        : null,
    },
    {
      title: 'Handgrip Strength',
      status: latestHandgrip
        ? recordStatus(latestLeftHandgrip) === 'Doing Well' || recordStatus(latestRightHandgrip) === 'Doing Well'
          ? 'Doing Well'
          : 'Aim to Improve'
        : null,
      metrics: latestHandgrip
        ? [
            {
              label: latestLeftHandgrip ? `Left Hand (${recordDate(latestLeftHandgrip)})` : 'Left Hand',
              value: latestLeftHandgrip ? `${latestLeftHandgrip.leftHandReading} kg` : '-',
              alert: recordStatus(latestLeftHandgrip) === 'Aim to Improve',
            },
            {
              label: latestRightHandgrip ? `Right Hand (${recordDate(latestRightHandgrip)})` : 'Right Hand',
              value: latestRightHandgrip ? `${latestRightHandgrip.rightHandReading} kg` : '-',
              alert: recordStatus(latestRightHandgrip) === 'Aim to Improve',
            },
          ]
        : null,
    },
    {
      title: 'Exhaustion',
      testDate: recordDate(latestExhaustion),
      status: recordStatus(latestExhaustion),
      metrics: latestExhaustion
        ? [
            {
              label: 'Everything Requires Effort',
              value: latestExhaustion.everythingRequireEffort,
              alert: isExhaustedAnswer(latestExhaustion.everythingRequireEffort),
            },
            {
              label: 'Cannot Get Going',
              value: latestExhaustion.cannotGetGoing,
              alert: isExhaustedAnswer(latestExhaustion.cannotGetGoing),
            },
          ]
        : null,
    },
    {
      title: 'Gait Speed',
      testDate: recordDate(latestGaitSpeed),
      status: recordStatus(latestGaitSpeed),
      metrics: latestGaitSpeed
        ? [{ label: 'Reading', value: `${latestGaitSpeed.reading} m/s`, alert: latestGaitSpeed.reading < 1.0 }]
        : null,
    },
    {
      title: 'Physical Activity',
      testDate: recordDate(latestPhysicalActivity),
      status: recordStatus(latestPhysicalActivity),
      metrics: latestPhysicalActivity
        ? [{ label: 'Time Spent Walking', value: latestPhysicalActivity.timeSpentInWalking }]
        : null,
    },
  ]

  const RANK1_TITLES = ['Cognition', 'AD8 (Memory Screening)', 'Mobility', 'Nutrition', 'Vision', 'Hearing', 'Mood']
  const rank1Domains = domains.filter((domain) => RANK1_TITLES.includes(domain.title))
  const rank2Domains = domains.filter((domain) => !RANK1_TITLES.includes(domain.title))

  const isCompleted = (domain: DomainCardProps) =>
    (!!domain.metrics && domain.metrics.length > 0) || !!domain.notRequired
  const missingDomains = domains.filter((domain) => !isCompleted(domain))

  const pushToken = (reportsData.notifications as any)?.expoPushToken as string | undefined

  const isAimToImprove = (status: string | null) => status?.toLowerCase() === 'aim to improve'
  const resultsNeedFollowUp = isAimToImprove(rank1Status) || isAimToImprove(rank2Status)
  const mobilityNeedsRetake = domains.find((domain) => domain.title === 'Mobility')?.status === 'Unable to Predict'
  const needsFollowUp = missingDomains.length > 0 || resultsNeedFollowUp || mobilityNeedsRetake

  const reminderTitleFor = () => {
    const activeReasonCount = [resultsNeedFollowUp, mobilityNeedsRetake, missingDomains.length > 0].filter(
      Boolean
    ).length

    if (activeReasonCount > 1) return 'Time for a Check-In at the Senior Care Centre'
    if (resultsNeedFollowUp) return 'Please Visit the Senior Care Centre'
    if (mobilityNeedsRetake) return 'Please Retake Your Mobility Test'
    if (missingDomains.length > 0) return 'You Have Unfinished Health Checks'
    return 'ENGAGE Health Check Reminder'
  }

  const openReminder = () => {
    const reasons: string[] = []
    if (resultsNeedFollowUp) {
      reasons.push(
        "We'd love to see you again! A quick follow-up visit to the Senior Care Centre would help us take good care of you."
      )
    }
    if (mobilityNeedsRetake) {
      reasons.push(
        "We weren't able to read the results from your last Mobility test — no worries, it happens! Could you give it another try next time you're free?"
      )
    }
    if (missingDomains.length > 0) {
      const missingList = missingDomains.map((domain) => domain.title).join(', ')
      reasons.push(
        `You're almost there — just a few more health checks to go (${missingList}) whenever you have a moment. We're here to help!`
      )
    }
    setReminderTitle(reminderTitleFor())
    setReminderMessage(
      reasons.length > 0
        ? reasons.join(' ')
        : "We'd love to see you at the Senior Care Centre for a quick check-in!"
    )
    setReminderOpen(true)
  }

  const sendReminder = async () => {
    if (!pushToken) return
    setReminderSending(true)
    try {
      await notificationService.send({
        userId: userName || '',
        expoPushToken: pushToken,
        title: reminderTitle || 'ENGAGE Health Check Reminder',
        body: reminderMessage,
      })
      message.success('Reminder sent to patient.')
      setReminderOpen(false)
    } catch (err) {
      console.error(err)
      if (err instanceof Error && err.message.includes('InvalidPushTokenException')) {
        message.error("This patient's device is no longer registered for notifications. They may have uninstalled the app or disabled notifications.")
      } else {
        message.error('Failed to send reminder. Please try again later.')
      }
    } finally {
      setReminderSending(false)
    }
  }

  const bloodPressureData = reportsData.bloodPressure
    .filter((entry) => {
      const recordDate = dayjs(entry.testTimestamptz.slice(0, 16).replace('T', ' '))
      return recordDate.isSameOrAfter(dateRange[0], 'day') && recordDate.isSameOrBefore(dateRange[1], 'day')
    })
    .flatMap((entry) => [
      { recordDate: entry.testTimestamptz.slice(0, 16).replace('T', ' '), type: 'systolic', value: entry.systolic },
      { recordDate: entry.testTimestamptz.slice(0, 16).replace('T', ' '), type: 'diastolic', value: entry.diastolic },
      { recordDate: entry.testTimestamptz.slice(0, 16).replace('T', ' '), type: 'heartRate', value: entry.heartRate },
    ])

  const sitToStandTimeData = reportsData.sitToStand
    .filter((entry) => {
      const recordDate = dayjs(entry.creationTimestamp)
      return (
        recordDate.isSameOrAfter(dateRange[0], 'day') &&
        recordDate.isSameOrBefore(dateRange[1], 'day') &&
        typeof sitToStandTime(entry) === 'number'
      )
    })
    .map((entry) => ({
      recordDate: dayjs(entry.creationTimestamp).format('YYYY-MM-DD'),
      value: sitToStandTime(entry) as number,
    }))

  const _span = isMobile ? 24 : 12

  return (
    <>
      <PageDrawer
        isOpen={drawer}
        closeDrawer={closeDrawer}
        _defaultStart={defaultStart}
        _defaultEnd={defaultEnd}
        _start={dateRange[0]}
        _end={dateRange[1]}
        setDateRange={setDateRange}
      />
      <ExportPreviewModal bundle={exportPreview} onClose={() => setExportPreview(null)} />
      <TabHeader border>
        <PageTitle text='Reports' />
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title='Download every past test result, one sheet per category'>
            <Button icon={<DownloadOutlined />} onClick={exportHistory}>
              Export
            </Button>
          </Tooltip>
          {needsFollowUp && (
            <Tooltip
              title={
                pushToken
                  ? 'Send a push notification nudging the patient to visit the Senior Care Centre'
                  : "This patient's app has not registered for notifications yet"
              }>
              <Button type='primary' icon={<BellOutlined />} disabled={!pushToken} onClick={openReminder}>
                Send Reminder
              </Button>
            </Tooltip>
          )}
        </div>
      </TabHeader>
      <PaddingBox>
        {[
          { title: 'Rank 1', sectionDomains: rank1Domains },
          { title: 'Rank 2', sectionDomains: rank2Domains },
        ].map(({ title, sectionDomains }) => {
          const completed = sectionDomains.filter(isCompleted)
          const missing = sectionDomains.filter((domain) => !isCompleted(domain))
          return (
            <div key={title} style={{ marginBottom: 24 }}>
              <PageSubtitle text={title} padding={false} />
              {completed.length > 0 ? (
                <div className='domain_card_grid_3' style={{ marginTop: 12 }}>
                  {completed.map((domain) => (
                    <DomainCard key={domain.title} {...domain} />
                  ))}
                </div>
              ) : (
                <Text type='secondary' style={{ display: 'block', marginTop: 12, fontSize: 13 }}>
                  No tests completed yet.
                </Text>
              )}
              {missing.length > 0 && (
                <Collapse
                  ghost
                  size='small'
                  style={{ marginTop: 8 }}
                  items={[
                    {
                      key: 'missing',
                      label: (
                        <Text type='secondary' style={{ fontSize: 13 }}>
                          Not Yet Completed ({missing.length})
                        </Text>
                      ),
                      children: (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {missing.map((domain) => (
                            <Tag key={domain.title} style={{ marginInlineEnd: 0 }}>
                              {domain.title}
                            </Tag>
                          ))}
                        </div>
                      ),
                    },
                  ]}
                />
              )}
            </div>
          )
        })}

        <Modal
          title='Send Reminder'
          open={reminderOpen}
          onOk={sendReminder}
          okText='Send'
          confirmLoading={reminderSending}
          onCancel={() => setReminderOpen(false)}>
          <Text type='secondary' style={{ fontSize: 12 }}>
            The patient will receive this as a push notification on their phone.
          </Text>
          <Text type='secondary' style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
            Title
          </Text>
          <Input
            style={{ marginTop: 4 }}
            maxLength={50}
            showCount
            value={reminderTitle}
            onChange={(event) => setReminderTitle(event.target.value)}
          />
          <Text type='secondary' style={{ display: 'block', marginTop: 12, fontSize: 12 }}>
            Message
          </Text>
          <Input.TextArea
            style={{ marginTop: 4 }}
            rows={4}
            value={reminderMessage}
            onChange={(event) => setReminderMessage(event.target.value)}
          />
        </Modal>

        <div style={{ marginTop: 32 }}>
          <TabHeader padding={false}>
            <PageSubtitle text='Trends' padding={false} />
            {isMobile ? (
              <Button type='primary' onClick={showDrawer} icon={<CalendarOutlined style={{ color: '#fff' }} />} />
            ) : (
              <Button type='primary' onClick={showDrawer}>
                <CalendarOutlined style={{ color: '#fff' }} />
                {`${dateRange[0].format('DD MMM YYYY')} - ${dateRange[1].format('DD MMM YYYY')}`}
              </Button>
            )}
          </TabHeader>
          <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
            <Col span={_span}>
              <ChartCard title='Blood Pressure' description={dateString}>
                {bloodPressureData.length > 0 ? (
                  <Column height={300} {...({ ...CONFIG.BloodPressure, data: bloodPressureData } as ColumnConfig)} />
                ) : (
                  <Text type='secondary'>No readings in this date range.</Text>
                )}
              </ChartCard>
            </Col>
            <Col span={_span}>
              <ChartCard title='Sit-to-Stand: Time Taken' description={dateString}>
                {sitToStandTimeData.length > 0 ? (
                  <Line
                    height={300}
                    {...({ ...CONFIG.BloodGlucose, data: sitToStandTimeData, meta: { ...CONFIG.BloodGlucose.meta, value: { alias: 'seconds' } } } as LineConfig)}
                  />
                ) : (
                  <Text type='secondary'>No sessions in this date range.</Text>
                )}
              </ChartCard>
            </Col>
          </Row>
        </div>
      </PaddingBox>
    </>
  )
}

export default Report
